import React, { useMemo, useRef, useState } from 'react';
import { Button } from '../../components/ui/Button';
import { useI18n } from '../../i18n/I18nContext';
import { cn } from '../../utils/cn';
import { PREVIEW_SAMPLE, renderPreview, type WhatsAppTemplateRecord } from './WhatsAppStore';

const BODY_MAX_LENGTH = 2000;

/**
 * One notification: a switch, the wording, and a preview of what the customer gets.
 *
 * Each card saves on its own rather than through a page-level form. The API upserts a
 * single row per template, so two people editing different notifications cannot
 * overwrite each other — batching them into one save would throw that away.
 */
export function WhatsAppTemplateCard({
  template,
  onSave,
  onReset,
}: {
  template: WhatsAppTemplateRecord;
  onSave: (code: string, body: string, enabled: boolean) => Promise<void>;
  onReset: (code: string) => Promise<void>;
}) {
  const { t } = useI18n();
  const [body, setBody] = useState(template.body);
  const [enabled, setEnabled] = useState(template.enabled);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const dirty = body !== template.body || enabled !== template.enabled;
  const tooLong = body.length > BODY_MAX_LENGTH;

  const preview = useMemo(() => {
    const values: Record<string, string> = {};
    template.variables.forEach((name) => {
      values[name] = PREVIEW_SAMPLE[name] ?? name;
    });
    return renderPreview(body, values);
  }, [body, template.variables]);

  /** Inserts at the caret rather than appending, so chips work mid-sentence. */
  const insertVariable = (name: string) => {
    const el = textareaRef.current;
    const token = `{{${name}}}`;

    if (!el) {
      setBody((prev) => prev + token);
      return;
    }

    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);

    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(template.code, body, enabled);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await onReset(template.code);
      setBody(template.defaultBody);
      setEnabled(template.defaultEnabled);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="flex flex-wrap items-start gap-3 border-b border-slate-100 px-4 py-4 sm:px-6 dark:border-slate-800">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {t(`settings.whatsapp.templates.${template.code}`)}
            </h3>
            {template.customised && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {t('settings.whatsapp.customised')}
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            {t(`settings.whatsapp.descriptions.${template.code}`)}
          </p>
        </div>

        <label className="flex shrink-0 cursor-pointer items-center gap-2">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
            {enabled ? t('settings.whatsapp.on') : t('settings.whatsapp.off')}
          </span>
          <span
            className={cn(
              'relative h-5 w-9 shrink-0 rounded-full transition-colors duration-200',
              enabled ? 'bg-red-600' : 'bg-slate-300 dark:bg-slate-600',
            )}
          >
            <span
              className={cn(
                'absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform duration-200',
                enabled ? 'translate-x-4' : 'translate-x-0',
              )}
            />
            <input
              type="checkbox"
              className="sr-only"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
          </span>
        </label>
      </div>

      <div className="space-y-4 px-4 py-5 sm:px-6">
        <div>
          <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
              {t('settings.whatsapp.message')}
            </span>
            <span
              className={cn(
                'text-xs tabular-nums',
                tooLong ? 'font-semibold text-red-600 dark:text-red-400' : 'text-slate-400 dark:text-slate-500',
              )}
            >
              {body.length} / {BODY_MAX_LENGTH}
            </span>
          </div>
          <textarea
            ref={textareaRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            spellCheck={false}
            className="w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-[13px] leading-relaxed text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-red-400 focus:ring-2 focus:ring-red-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
          />
        </div>

        <div>
          <div className="mb-1.5 text-xs font-medium text-slate-600 dark:text-slate-400">
            {t('settings.whatsapp.variables')}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {template.variables.map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => insertVariable(name)}
                className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-[11px] text-slate-700 transition hover:border-red-300 hover:bg-red-50 hover:text-red-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-red-800 dark:hover:bg-slate-700"
              >
                {`{{${name}}}`}
              </button>
            ))}
          </div>
        </div>

        <div>
          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            className="text-xs font-medium text-red-600 underline-offset-2 hover:underline dark:text-red-400"
          >
            {showPreview ? t('settings.whatsapp.hidePreview') : t('settings.whatsapp.showPreview')}
          </button>
          {showPreview && (
            <div className="mt-2 whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-[13px] leading-relaxed text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
              {preview || t('settings.whatsapp.previewEmpty')}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4 dark:border-slate-800">
          <Button size="sm" onClick={handleSave} disabled={!dirty || tooLong || saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleReset}
            disabled={resetting || (!template.customised && !dirty)}
          >
            {resetting ? t('common.saving') : t('settings.whatsapp.resetToDefault')}
          </Button>
          {tooLong && (
            <span className="text-xs text-red-600 dark:text-red-400">
              {t('settings.whatsapp.tooLong', { max: BODY_MAX_LENGTH })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
